import { FC, useState, useEffect, useRef } from 'react';

interface InputFieldProps {
    value: string;
    onSave: (value: string) => void;
    onCancel?: () => void;
    className?: string;
    placeholder?: string;
    autoFocus?: boolean;
}

const InputField: FC<InputFieldProps> = ({
    value,
    onSave,
    onCancel,
    className = "bg-transparent border border-gray-300 rounded px-1 font-bold w-full inline-block overflow-visible",
    placeholder,
    autoFocus = true
}) => {
    const [inputValue, setInputValue] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);
    const isInitialMount = useRef(true);

    // Update input value when value prop changes
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Focus and select all text when component mounts or becomes visible
    useEffect(() => {
        if (autoFocus && inputRef.current && isInitialMount.current) {
            inputRef.current.focus();
            inputRef.current.select();
            isInitialMount.current = false;
        }
    }, [autoFocus, value]);

    const handleSave = () => {
        if (inputValue.trim() === "") {
            setInputValue(value);
            onCancel?.();
            return;
        }
        onSave(inputValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleSave();
        } else if (e.key === "Escape") {
            setInputValue(value);
            onCancel?.();
        }
    };

    return (
        <input
            ref={inputRef}
            autoFocus={autoFocus}
            className={className}
            value={inputValue}
            placeholder={placeholder}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
        />
    );
};

export default InputField; 